import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, BookOpen, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { contactInfo } from '../../lib/data';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPost(data);
          
          // Fetch related posts
          const allPostsResponse = await fetch(`${API_URL}/api/blog`);
          if (allPostsResponse.ok) {
            const allPosts = await allPostsResponse.json();
            setRelatedPosts(allPosts.filter(p => p.slug !== slug).slice(0, 2));
          }
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Failed to fetch post:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, API_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sage-600 animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/resources/blog" replace />;
  }

  return (
    <div data-testid={`blog-post-${slug}`}>
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/resources/blog" 
              className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-700 mb-10 transition-colors group font-medium"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Link>
            
            <div className="max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-xs font-semibold text-sage-600 uppercase tracking-wide">
                  {post.category}
                </span>
                <span className="flex items-center gap-1 text-xs text-charcoal/50">
                  <Clock className="w-3 h-3" />
                  {post.read_time}
                </span>
                <span className="flex items-center gap-1 text-xs text-charcoal/50">
                  <Calendar className="w-3 h-3" />
                  {new Date(post.created_at).toLocaleDateString('en-IE', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </span>
              </div>
              
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-charcoal leading-tight">
                {post.title}
              </h1>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {/* Featured image */}
                {post.featured_image ? (
                  <img 
                    src={post.featured_image.startsWith('/api') ? `${API_URL}${post.featured_image}` : post.featured_image} 
                    alt={post.title}
                    className="w-full rounded-2xl mb-10 shadow-soft"
                  />
                ) : (
                  <div className="bg-sage-100 rounded-2xl aspect-video flex items-center justify-center mb-10">
                    <BookOpen className="w-16 h-16 text-sage-300" />
                  </div>
                )}

                {/* Article content */}
                <div className="prose prose-lg max-w-none">
                  <p className="text-xl text-charcoal/70 leading-relaxed mb-8">
                    {post.excerpt}
                  </p>
                  
                  <div className="text-charcoal/70 leading-relaxed whitespace-pre-wrap">
                    {post.content}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="sticky top-28 space-y-8"
              >
                {/* CTA Card */}
                <Card className="bg-sage-50 border-0 shadow-soft rounded-2xl">
                  <CardContent className="p-8 text-center">
                    <h3 className="font-serif text-xl font-medium text-charcoal mb-4">
                      Have Questions?
                    </h3>
                    <p className="text-charcoal/60 text-sm mb-6 leading-relaxed">
                      Get in touch to discuss how this topic relates to your health.
                    </p>
                    <Button
                      asChild
                      className="w-full bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full gap-2 py-6 font-semibold"
                      data-testid="blog-post-whatsapp"
                    >
                      <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-5 h-5" />
                        Message Ali
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <Card className="bg-white border-0 shadow-soft rounded-2xl">
                    <CardContent className="p-8">
                      <h3 className="font-serif text-xl font-medium text-charcoal mb-6">
                        Related Articles
                      </h3>
                      <div className="space-y-5">
                        {relatedPosts.map((relatedPost) => (
                          <Link
                            key={relatedPost.id}
                            to={`/resources/blog/${relatedPost.slug}`}
                            className="block group"
                          >
                            <h4 className="font-medium text-charcoal group-hover:text-sage-600 transition-colors mb-1">
                              {relatedPost.title}
                            </h4>
                            <p className="text-sm text-charcoal/50">
                              {relatedPost.category} • {relatedPost.read_time}
                            </p>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
